
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { X, ShieldCheck, BarChart3, Settings as Cog } from "lucide-react";
import { isIndexablePath } from "@/lib/useCanonical";
import { applyHeycatchConsent } from "@/lib/heycatchAnalytics";

const KEY = "zy_cookie_prefs_v1";

const defaultPrefs = { necessary: true, functional: true, analytics: false, marketing: false };

const CookieCtx = createContext({ open: () => {}, prefs: defaultPrefs });
export const useCookieSettings = () => useContext(CookieCtx);

/**
 * Provides:
 *  - A cookie consent banner on first visit (bottom-right)
 *  - A "Cookie settings" modal openable via context (footer link, Cookie Policy page)
 *  - Preferences persisted in localStorage under `zy_cookie_prefs_v1`
 */
export function CookieSettingsProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [saveError, setSaveError] = useState("");

  // Initialise PostHog only after an explicit, persisted analytics opt-in.
  // public/index.html owns the lazy SDK initializer.
  const applyPosthogConsent = useCallback((analyticsAllowed) => {
    if (typeof window === "undefined") return;

    try {
      if (analyticsAllowed && window.__zyAnalyticsDeniedForPage !== true) {
        window.__zyInitPosthog?.();

        // SDK calls are queued by the snippet while the network script loads.
        window.posthog?.opt_in_capturing?.();

        if (isIndexablePath(window.location.pathname)) {
          window.posthog?.startSessionRecording?.();
        } else {
          window.posthog?.stopSessionRecording?.();
        }
      } else if (window.__zyPosthogInited) {
        // Do not initialise the SDK just to opt out.
        window.posthog?.stopSessionRecording?.();
        window.posthog?.opt_out_capturing?.();
        window.posthog?.reset?.();
      }
    } catch {
      // Fail closed: never initiate PostHog tracking on an error.
    }
  }, []);

  useEffect(() => {
    let effective = defaultPrefs;

    try {
      const raw = localStorage.getItem(KEY);

      if (raw) {
        const parsed = JSON.parse(raw);

        effective = {
          necessary: true,
          functional: parsed?.functional === true,
          analytics: parsed?.analytics === true,
          marketing: parsed?.marketing === true,
        };

        setPrefs(effective);
      } else {
        setBannerVisible(true);
      }
    } catch {
      setBannerVisible(true);
    }

    window.__zyApplyGoogleConsent?.();
    applyPosthogConsent(effective.analytics);
    applyHeycatchConsent(effective.analytics);
  }, [applyPosthogConsent]);

  const save = useCallback((next) => {
    const normalized = {
      necessary: true,
      functional: next.functional === true,
      analytics: next.analytics === true,
      marketing: next.marketing === true,
    };

    // Do not activate analytics if consent cannot be persisted.
    let persisted = false;

    try {
      localStorage.setItem(KEY, JSON.stringify(normalized));
      persisted = true;
    } catch {
      /* storage blocked */
    }

    const effective = persisted
      ? normalized
      : { ...normalized, analytics: false, marketing: false };

    // Keep a failed/rejected choice authoritative for this loaded page.
    if (typeof window !== "undefined") {
      window.__zyAnalyticsDeniedForPage = !persisted || !effective.analytics;
      window.__zyMarketingDeniedForPage = !persisted || !effective.marketing;
    }
    setPrefs(effective);
    setSaveError(persisted ? "" : "Your choice could not be saved in this browser. Please check browser storage settings and try again. A previous saved choice may still apply when you return.");
    if (persisted) {
      setBannerVisible(false);
      setOpen(false);
    } else {
      setBannerVisible(true);
    }

    // The HTML initializer independently verifies persisted consent before loading.
    if (typeof window !== "undefined") {
      window.__zyApplyGoogleConsent?.();
    }

    applyPosthogConsent(effective.analytics);
    applyHeycatchConsent(effective.analytics);
    if (persisted && effective.marketing) window.__zyInitFirstPromoter?.();

    // Failed persistence must not re-read and reapply an old analytics opt-in.
    if (!persisted) return;

    // Notify the route guard immediately when consent changes.
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new Event("zy:analytics-consent-changed")
      );
      window.dispatchEvent(new Event("zy:marketing-consent-changed"));
    }
  }, [applyPosthogConsent]);

  return (
    <CookieCtx.Provider value={{ open: () => setOpen(true), prefs }}>
      {children}

      {bannerVisible && !open && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
          className="fixed bottom-4 right-4 z-50 max-w-sm w-[min(92vw,420px)] bg-white border border-[#eee] rounded-xl shadow-[0_10px_40px_-8px_rgba(10,22,40,0.25)] p-5"
        >
          <p className="text-[13.5px] font-semibold text-black mb-1">
            We respect your privacy
          </p>

          <p className="text-[12.5px] text-[#555] leading-relaxed">
            We use strictly necessary cookies to run Zynthoro. Functional, analytics and marketing/referral tracking cookies are optional. Read our{" "}
            <a
              href="/legal/cookie-policy"
              className="text-[#1A4FFF] font-semibold"
            >
              Cookie Policy
            </a>.
          </p>

          {saveError && <p role="alert" className="mt-3 text-sm text-amber-800">{saveError}</p>}

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() =>
                save({
                  necessary: true,
                  functional: false,
                  analytics: false,
                  marketing: false,
                })
              }
              className="px-3 py-1.5 text-[12.5px] font-medium rounded-md border border-[#eee] text-[#333]"
              data-testid="cookie-reject"
            >
              Reject all
            </button>

            <button
              onClick={() => setOpen(true)}
              className="px-3 py-1.5 text-[12.5px] font-medium rounded-md border border-[#eee] text-[#333]"
              data-testid="cookie-customize"
            >
              Customize
            </button>

            <button
              onClick={() =>
                save({
                  necessary: true,
                  functional: true,
                  analytics: true,
                  marketing: true,
                })
              }
              className="px-3 py-1.5 text-[12.5px] font-semibold rounded-md text-white"
              style={{ background: "#1A4FFF" }}
              data-testid="cookie-accept"
            >
              Accept all
            </button>
          </div>
        </div>
      )}

      {open && (
        <CookieSettingsModal
          prefs={prefs}
          saveError={saveError}
          onClose={() => setOpen(false)}
          onSave={save}
        />
      )}
    </CookieCtx.Provider>
  );
}

function Row({ icon: Icon, title, desc, checked, onChange, locked, testid }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#f3f3f3] last:border-0">
      <div className="w-8 h-8 rounded-md flex items-center justify-center bg-[#F4F6FB] shrink-0">
        <Icon size={15} style={{ color: "#1A4FFF" }} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold text-black">{title}</p>
        <p className="text-[12.5px] text-[#666] mt-0.5 leading-relaxed">
          {desc}
        </p>
      </div>

      <label className="inline-flex items-center cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          disabled={locked}
          onChange={(e) => onChange?.(e.target.checked)}
          className="sr-only peer"
          data-testid={testid}
        />

        <span
          className={`w-9 h-5 rounded-full transition-colors ${
            locked
              ? "bg-[#1A4FFF] opacity-50"
              : checked
                ? "bg-[#1A4FFF]"
                : "bg-[#ddd]"
          } relative`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
              checked ? "translate-x-4" : ""
            }`}
          />
        </span>
      </label>
    </div>
  );
}

function CookieSettingsModal({ prefs, saveError, onClose, onSave }) {
  const [local, setLocal] = useState(prefs);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-label="Cookie settings"
        data-testid="cookie-settings-modal"
        className="relative bg-white rounded-2xl shadow-xl w-[min(92vw,520px)] max-h-[88vh] overflow-y-auto p-6"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded hover:bg-[#F4F6FB]"
          aria-label="Close"
          data-testid="cookie-modal-close"
        >
          <X size={18} />
        </button>

        <p className="zy-eyebrow mb-2">Cookie settings</p>

        <h3 className="text-[22px] font-bold tracking-tight">
          Manage your preferences
        </h3>

        <p className="text-[13.5px] text-[#555] mt-2">
          Toggle the types of cookies you want Zynthoro to use. Strictly
          necessary cookies are always on so the platform can function.
        </p>

        <div className="mt-5">
          <Row
            icon={ShieldCheck}
            title="Strictly necessary"
            desc="Session, authentication and security cookies. Always on."
            checked
            locked
            testid="cookie-pref-necessary"
          />

          <Row
            icon={Cog}
            title="Functional"
            desc="Remember your UI preferences (theme, language)."
            checked={local.functional}
            onChange={(v) =>
              setLocal((p) => ({ ...p, functional: v }))
            }
            testid="cookie-pref-functional"
          />

          <Row
            icon={BarChart3}
            title="Analytics"
            desc="Usage analytics via Google and PostHog, including session replay on selected public pages. Optional; you can change your choice here."
            checked={local.analytics}
            onChange={(v) =>
              setLocal((p) => ({ ...p, analytics: v }))
            }
            testid="cookie-pref-analytics"
          />
          <Row
            icon={BarChart3}
            title="Marketing & affiliate referrals"
            desc="FirstPromoter referral attribution and campaign conversion tracking. Optional; no tracking script loads without this consent."
            checked={local.marketing}
            onChange={(v) => setLocal((p) => ({ ...p, marketing: v }))}
            testid="cookie-pref-marketing"
          />
        </div>

        {saveError && <p role="alert" className="mt-4 text-sm text-amber-800">{saveError}</p>}

        <div className="flex flex-wrap gap-2 justify-end mt-6">
          <button
            onClick={() =>
              onSave({
                necessary: true,
                functional: false,
                analytics: false,
                marketing: false,
              })
            }
            className="px-3.5 py-2 text-[13px] font-medium rounded-md border border-[#eee] text-[#333]"
            data-testid="cookie-modal-reject"
          >
            Reject non-essential
          </button>

          <button
            onClick={() => onSave(local)}
            className="px-4 py-2 text-[13px] font-semibold rounded-md text-white"
            style={{ background: "#1A4FFF" }}
            data-testid="cookie-modal-save"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}

/** Footer-style link that opens the cookie settings modal (Fix 11). */
export function CookieSettingsLink({ className = "" }) {
  const { open } = useCookieSettings();

  return (
    <button
      type="button"
      onClick={open}
      data-testid="footer-cookie-settings"
      className={`text-[14px] text-white/70 hover:text-white underline-offset-2 hover:underline ${className}`}
    >
      Cookie settings
    </button>
  );
}

